import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation, NavLink } from 'react-router-dom';
import { Avatar, InfoIcon } from 'modules/Core/Common';
import { useMediaQuery, MOBILE_SIZE } from 'modules/Core/Hooks';
import { prettyUserType } from 'modules/Users/Permissions/UserType';
import { MenuIcon, CloseIcon } from '@edusynch/edusynch-svg-icons';
import { useSelector } from 'react-redux';
import { Selectors as AuthSelectors } from 'modules/Auth/AuthSlice';
import { useLayout } from '../Hooks/useLayout';
import { HeaderDropdown } from './HeaderDropdown';
import { HeaderInstitutionIds } from './HeaderInstitutionIds';
import * as S from './Header.style';
import defaultLogo from 'assets/common/logo-edusynch.png';

const Header = ({
  onLogoutClick,
  user,
  whitelabelName,
  logo,
  outcome,
  openedResponsive,
  toggleResponsiveSidebarOpen,
  toggleResponsiveSidebarClose,
  ...props
}) => {
  const { pathname } = useLocation();
  const { topMargin } = useLayout();
  const isMobile = useMediaQuery(MOBILE_SIZE);
  const session = useSelector(AuthSelectors.selectAuthData);
  const displayName =
    session?.user?.group?.name || whitelabelName || 'EduSynch';

  const isSubmissionView = useMemo(() => {
    return pathname?.includes('submissions') && pathname?.includes('answers');
  }, [pathname]);

  return (
    <S.Header
      noShadow={isSubmissionView}
      $isMobile={isMobile}
      style={{ top: `${topMargin || 0}px` }}
    >
      {isMobile ? (
        <>
          <S.MobileLogoWhitelabelWrapper>
            <S.MobileLogoWrapper as={NavLink} to="/dashboard">
              <img src={logo || defaultLogo} alt="EduSynch logo" />
            </S.MobileLogoWrapper>
            <S.MobileWhitelabelText>
              <S.MobileYouAreIn>You are in</S.MobileYouAreIn>
              <S.MobileWhitelabelName>{displayName}</S.MobileWhitelabelName>
              <HeaderInstitutionIds user={session?.user} isMobile />
            </S.MobileWhitelabelText>
          </S.MobileLogoWhitelabelWrapper>

          <S.MobileRightContainer>
            <S.AvatarWrapper>
              <Avatar
                src={user?.avatar?.thumb?.url}
                name={user?.name}
                size="4.2rem"
                data-cy="headerbar-avatar"
              />
            </S.AvatarWrapper>
            <S.HamburgerButton
              type="button"
              aria-label={openedResponsive ? 'Close menu' : 'Open menu'}
              data-test="dropdown-logout"
              data-cy="header-dropdown"
              onClick={
                openedResponsive
                  ? toggleResponsiveSidebarClose
                  : toggleResponsiveSidebarOpen
              }
            >
              {openedResponsive ? (
                <CloseIcon color="currentColor" width={16} height={16} />
              ) : (
                <MenuIcon
                  color="currentColor"
                  type="medium"
                  width={16}
                  height={16}
                />
              )}
            </S.HamburgerButton>
          </S.MobileRightContainer>
        </>
      ) : (
        <>
          <S.DesktopRightContainer>
            <HeaderInstitutionIds user={session?.user} />

            <InfoIcon
              thin
              xsmall
              xxsmall
              title={prettyUserType(user?.user_type)}
              before={
                <Avatar
                  src={user?.avatar?.thumb?.url}
                  name={user?.name}
                  data-cy="headerbar-avatar"
                />
              }
              data-test="dash-student-section"
            >
              {user?.name}
            </InfoIcon>

            <HeaderDropdown
              logout={onLogoutClick}
              outcome={outcome}
              {...props}
            />
          </S.DesktopRightContainer>
        </>
      )}
    </S.Header>
  );
};

Header.propTypes = {
  onLogoutClick: PropTypes.func,
  user: PropTypes.object,
  whitelabelName: PropTypes.string,
  logo: PropTypes.string,
  outcome: PropTypes.any,
  openedResponsive: PropTypes.bool,
  toggleResponsiveSidebarOpen: PropTypes.func,
  toggleResponsiveSidebarClose: PropTypes.func,
};

export default React.memo(Header);
